import React, { useEffect, useState } from 'react';
import { parseServerTime } from '@/lib/serverTime';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Monitor, Smartphone, Tablet, Globe, Clock, LogOut, RefreshCw, CheckCircle2 } from 'lucide-react';

export const ActiveSessions: React.FC = () => {
  const { activeSessions, fetchActiveSessions, terminateSession } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchActiveSessions();
    } catch (err: any) {
      setError(err.message || '세션 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleTerminateSession = async (sessionId: string) => {
    if (!window.confirm('이 세션을 종료하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await terminateSession(sessionId);
      setSuccess('세션이 종료되었습니다.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || '세션 종료에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (deviceInfo: string) => {
    const lowerDevice = deviceInfo.toLowerCase();
    if (lowerDevice.includes('mobile') || lowerDevice.includes('android') || lowerDevice.includes('iphone')) {
      return <Smartphone className="w-5 h-5 text-blue-600" />;
    }
    if (lowerDevice.includes('tablet') || lowerDevice.includes('ipad')) {
      return <Tablet className="w-5 h-5 text-green-600" />;
    }
    return <Monitor className="w-5 h-5 text-gray-600" />;
  };

  const formatDateTime = (dateString: string) => {
    const date = parseServerTime(dateString);
    return date?.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? '-';
  };

  const getTimeDifference = (dateString: string) => {
    const now = new Date();
    const past = parseServerTime(dateString) ?? now;
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}시간 전`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}일 전`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>활성 세션 관리</CardTitle>
            <CardDescription>
              현재 로그인되어 있는 기기 목록을 확인하고 관리할 수 있습니다
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadSessions}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {loading && activeSessions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>세션 정보를 불러오는 중...</p>
          </div>
        ) : activeSessions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Monitor className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>활성 세션이 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeSessions.map((session) => (
              <div
                key={session.sessionId}
                className={`border rounded-lg p-4 ${
                  session.isCurrent ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex gap-3 flex-1">
                    {getDeviceIcon(session.deviceInfo)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm">
                          {session.deviceInfo.includes('Windows') && 'Windows PC'}
                          {session.deviceInfo.includes('Mac') && 'Mac'}
                          {session.deviceInfo.includes('Linux') && 'Linux'}
                          {session.deviceInfo.includes('Android') && 'Android 기기'}
                          {session.deviceInfo.includes('iPhone') && 'iPhone'}
                          {session.deviceInfo.includes('iPad') && 'iPad'}
                          {!session.deviceInfo.match(/Windows|Mac|Linux|Android|iPhone|iPad/) && '알 수 없는 기기'}
                        </h4>
                        {session.isCurrent && (
                          <Badge className="bg-green-500 text-white text-xs">
                            현재 세션
                          </Badge>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3 h-3" />
                          <span>IP: {session.ipAddress}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          <span>로그인: {formatDateTime(session.loginTime)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          <span>마지막 활동: {getTimeDifference(session.lastActivityTime)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {!session.isCurrent && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleTerminateSession(session.sessionId)}
                      disabled={loading}
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      종료
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 border-t">
          <p className="text-xs text-gray-500">
            💡 보안을 위해 의심스러운 세션이 발견되면 즉시 종료하고 비밀번호를 변경하세요.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
